import { useEffect, useRef, useState } from 'react';
import type { Conversation, Counters, InboxFilter } from '../../../types/whatsapp';
import type { ListView } from '../../../whatsapp/conversationList';
import { listTimeLabel } from '../../../whatsapp/clock';
import { Switch } from '../Switch';

export interface ConversationListPanelProps {
  conversations: Conversation[];
  selectedId: number | null;
  view: ListView;
  counters: Counters | null;
  loading: boolean;
  error: boolean;
  hasMore: boolean;
  timeZone: string;
  nowMs: number;
  onFilter: (f: InboxFilter) => void;
  onUnanswered: (v: boolean) => void;
  onQuery: (q: string) => void;
  onSelect: (id: number) => void;
  onLoadMore: () => void;
  onRetry: () => void;
}

const SEARCH_DEBOUNCE_MS = 300;
const SKELETON_ROWS = 5;

const TABS: { value: InboxFilter; label: string; withCounter: boolean }[] = [
  { value: 'all', label: 'Todas', withCounter: true },
  { value: 'assistant', label: 'Asistente', withCounter: false },
  { value: 'human-agent', label: 'Equipo', withCounter: true },
];

const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2';

/** Initials from the first letter of the first two words; phone-only titles use "+5". */
const initialsOf = (title: string): string => {
  const t = title.trim();
  if (t.startsWith('+')) return '+5';
  const letters = t
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.match(/\p{L}/u)?.[0] ?? '')
    .join('');
  return letters ? letters.toUpperCase() : '+5';
};

const WarningIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" role="img" aria-label="Posible urgencia">
    <title>Posible urgencia</title>
    <path d="M12 9v4" />
    <path d="M12 17h.01" />
    <path d="M10.3 3.9 2.4 18a2 2 0 0 0 1.7 3h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
  </svg>
);

const SparkIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" role="img" aria-label="Asignada al asistente">
    <title>Asignada al asistente</title>
    <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3Z" />
  </svg>
);

const PersonIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" role="img" aria-label="Asignada al equipo">
    <title>Asignada al equipo</title>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21c0-4 3.6-7 8-7s8 3 8 7" />
  </svg>
);

const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
);

const SkeletonRow = () => (
  <li className="flex items-center gap-2.5 py-3 pl-2 pr-3 animate-pulse" aria-hidden="true">
    <span className="size-2 flex-none" />
    <span className="size-10 flex-none rounded-full bg-stone-200" />
    <span className="flex min-w-0 flex-1 flex-col gap-2">
      <span className="block h-3 w-2/3 rounded bg-stone-200" />
      <span className="block h-3 w-1/2 rounded bg-stone-100" />
    </span>
  </li>
);

const Row = ({
  conversation: c,
  selected,
  timeZone,
  nowMs,
  onSelect,
}: {
  conversation: Conversation;
  selected: boolean;
  timeZone: string;
  nowMs: number;
  onSelect: (id: number) => void;
}) => {
  const awaiting = c.awaiting_reply;
  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(c.id)}
        aria-current={selected ? 'true' : undefined}
        className={`flex w-full items-center gap-2.5 rounded-[14px] py-3 pl-2 pr-3 text-left transition-colors ${
          selected ? 'bg-blue-50' : 'hover:bg-stone-50'
        } ${FOCUS_RING}`}
      >
        <span className="flex w-4 flex-none items-center justify-center text-red-600">
          {c.urgent ? (
            <WarningIcon />
          ) : awaiting ? (
            <span className="size-2 rounded-full bg-red-600" />
          ) : (
            <span className="size-2" />
          )}
        </span>

        <span className="flex size-10 flex-none items-center justify-center rounded-full border border-stone-200 bg-stone-100 text-[13px] font-semibold text-stone-600">
          {initialsOf(c.title)}
        </span>

        <span className="flex min-w-0 flex-1 flex-col">
          <span className="flex items-center gap-2">
            <span className={`min-w-0 flex-1 truncate text-sm text-stone-900 ${awaiting ? 'font-bold' : 'font-medium'}`}>
              {c.title}
            </span>
            <span
              className={`font-geist-mono flex-none text-xs ${awaiting ? 'font-semibold text-red-700' : 'font-normal text-stone-600'}`}
            >
              {listTimeLabel(c.last_message_at, timeZone, nowMs)}
            </span>
            <span className="flex flex-none items-center">
              {c.assigned_to === 'assistant' ? <span className="flex text-blue-700"><SparkIcon /></span> : <span className="flex text-amber-800"><PersonIcon /></span>}
            </span>
          </span>
          <span
            className={`mt-0.5 block truncate text-[13px] ${awaiting ? 'font-semibold text-stone-800' : 'font-normal text-stone-600'}`}
          >
            {c.last_message_preview}
          </span>
        </span>

        {awaiting && <span className="sr-only">Sin responder</span>}
      </button>
    </li>
  );
};

export const ConversationListPanel = ({
  conversations,
  selectedId,
  view,
  counters,
  loading,
  error,
  hasMore,
  timeZone,
  nowMs,
  onFilter,
  onUnanswered,
  onQuery,
  onSelect,
  onLoadMore,
  onRetry,
}: ConversationListPanelProps) => {
  const [q, setQ] = useState(view.q);
  // The last query reported to the parent, so an echo of it does not re-trigger a search.
  const lastSent = useRef(view.q);
  const onQueryRef = useRef(onQuery);
  onQueryRef.current = onQuery;

  // Follows external changes of the query (e.g. a reset) without fighting the user's typing.
  useEffect(() => {
    if (view.q === lastSent.current) return;
    lastSent.current = view.q;
    setQ(view.q);
  }, [view.q]);

  useEffect(() => {
    if (q === lastSent.current) return;
    const timer = window.setTimeout(() => {
      lastSent.current = q;
      onQueryRef.current(q);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [q]);

  const tabCount = (filter: InboxFilter): number =>
    filter === 'all' ? (counters?.unanswered ?? 0) : filter === 'human-agent' ? (counters?.unanswered_human_agent ?? 0) : 0;
  const filtered = view.q !== '' || view.unanswered || view.filter !== 'all';
  // A failed refresh keeps the rows already loaded; the error block only replaces an empty list.
  const blocked = error && conversations.length === 0;
  const showSkeleton = loading && conversations.length === 0 && !error;
  const showEmpty = !loading && !error && conversations.length === 0;

  return (
    <section
      aria-label="Conversaciones"
      className="flex h-full min-h-0 flex-col overflow-hidden rounded-[20px] border border-stone-200 bg-white font-geist"
    >
      <div className="flex flex-col gap-3 px-4 pb-3 pt-4">
        <div className="relative">
          <span className="pointer-events-none absolute left-4 top-1/2 flex -translate-y-1/2 text-stone-500">
            <SearchIcon />
          </span>
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nombre o teléfono"
            aria-label="Buscar conversación"
            className={`h-11 w-full rounded-full border border-stone-200 bg-stone-50 pl-11 pr-4 text-sm text-stone-900 placeholder:text-stone-500 focus:border-blue-600 focus:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600`}
          />
        </div>

        <div role="tablist" aria-label="Asignación" className="flex items-center gap-1">
          {TABS.map((tab) => {
            const active = view.filter === tab.value;
            return (
              <button
                key={tab.value}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => onFilter(tab.value)}
                className={`relative flex h-10 items-center gap-1.5 rounded-lg px-3 text-sm font-semibold ${
                  active ? 'text-stone-900' : 'text-stone-600 hover:text-stone-900'
                } ${FOCUS_RING}`}
              >
                {tab.label}
                {tab.withCounter && tabCount(tab.value) > 0 && (
                  <span className="font-geist-mono text-xs font-semibold text-red-700">{tabCount(tab.value)}</span>
                )}
                {active && <span aria-hidden="true" className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-blue-600" />}
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-between gap-3">
          <span className="text-[13px] text-stone-600">Solo sin responder</span>
          <Switch
            checked={view.unanswered}
            onChange={() => onUnanswered(!view.unanswered)}
            label="Solo sin responder"
          />
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto border-t border-stone-200 px-2 py-2">
        {blocked && (
          <div role="alert" className="flex min-h-[200px] flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
            <p className="m-0 text-sm font-semibold text-stone-900">No se pudo cargar la bandeja</p>
            <button
              type="button"
              onClick={onRetry}
              className={`h-10 rounded-full border border-primary bg-white px-[18px] text-sm font-semibold text-primary hover:bg-blue-50 ${FOCUS_RING}`}
            >
              Reintentar
            </button>
          </div>
        )}

        {showSkeleton && (
          <ul aria-busy="true" className="m-0 flex list-none flex-col gap-0.5 p-0">
            {Array.from({ length: SKELETON_ROWS }, (_, i) => (
              <SkeletonRow key={i} />
            ))}
          </ul>
        )}

        {showEmpty && (
          <div className="flex min-h-[200px] flex-1 flex-col items-center justify-center gap-1.5 p-6 text-center">
            <p className="m-0 text-sm font-semibold text-stone-900">
              {filtered ? 'Sin resultados' : 'Sin conversaciones'}
            </p>
            <p className="m-0 max-w-[260px] text-[13px] leading-[1.45] text-stone-600">
              {filtered
                ? 'Ninguna conversación coincide con los filtros.'
                : 'Los mensajes nuevos de tus pacientes aparecerán aquí.'}
            </p>
          </div>
        )}

        {!blocked && conversations.length > 0 && (
          <ul aria-busy={loading} className="m-0 flex list-none flex-col gap-0.5 p-0">
            {conversations.map((c) => (
              <Row
                key={c.id}
                conversation={c}
                selected={c.id === selectedId}
                timeZone={timeZone}
                nowMs={nowMs}
                onSelect={onSelect}
              />
            ))}
          </ul>
        )}

        {!blocked && hasMore && (
          <div className="flex justify-center pt-2">
            <button
              type="button"
              onClick={onLoadMore}
              disabled={loading}
              className={`h-9 rounded-full px-4 text-sm font-semibold text-primary hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50 ${FOCUS_RING}`}
            >
              Cargar más
            </button>
          </div>
        )}
      </div>
    </section>
  );
};
